import { useCallback, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { DocumentProcessingStatusType, DocumentRecord, DocumentState } from '../../../models/Document'
import { EmailAttachmentMetadata } from '../../../models/EmailAttachments'
import { AIAgentType } from '../../../models/AIAgent'

import { useGetAllContactChangesQuery } from '../../../store/meetingSupport/meetingSupport'
import { useGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useGetAllDocumentsQuery, useGetDocumentsQuery, useGetEmailAttachmentReferencesQuery } from '../../../store/ai/documents'
import { useGetEmailAttachmentsQuery } from '../../../store/integrations/integrations'
import { useGetMeQuery } from '../../../store/user/user'
import { emailAttachmentQueueKey } from '../hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import {
  selectDocumentContactsFilter,
  selectDocumentCreatedByFilter,
  selectDocumentEndDateFilter,
  selectDocumentStartDateFilter,
  selectDocumentTypeFilter,
} from '../../../store/processDocument/documentFilter'
import { useAppSelector } from '../../../store/hooks'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { useGetRetentionPoliciesQuery } from '../../../store/account/account'
import { RetentionResourceType, RetentionRuleType } from '../../../models/RetentionPolicy'
import { useAccountHasDocumentZdrPolicy } from '../hooks'

const _SEARCH_TERM_MIN_LENGTH = 3

const groupByBatch = (documents: DocumentRecord[]): Record<string, DocumentRecord[]> => {
  const acc: Record<string, DocumentRecord[]> = {}

  for (const document of documents) {
    const key = document.documentBatchId ?? document.id

    const group = acc[key] ?? []
    group.push(document)

    acc[key] = group
  }

  return acc
}

export const useDocuments = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const contactIds = useAppSelector(selectDocumentContactsFilter)
  const documentTypeId = useAppSelector(selectDocumentTypeFilter)
  const createdByUserIds = useAppSelector(selectDocumentCreatedByFilter)
  const startDate = useAppSelector(selectDocumentStartDateFilter)
  const endDate = useAppSelector(selectDocumentEndDateFilter)

  const effectiveCreatedByUserIds = createdByUserIds ?? [currentUserId]

  const { page, pageSize, searchTerm } = useDocumentSearchParams()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400)

  const activeSearchTerm = useMemo(
    () => (debouncedSearchTerm && debouncedSearchTerm.length >= _SEARCH_TERM_MIN_LENGTH ? debouncedSearchTerm : null),
    [debouncedSearchTerm],
  )

  const { data: retentionPoliciesResponse, isFetching: retentionPoliciesFetching } = useGetRetentionPoliciesQuery({ accountId })

  const accountHasZDRPolicy = useMemo(
    () =>
      !!retentionPoliciesResponse?.data.find(({ resource, rule }) => resource.type === RetentionResourceType.DOCUMENT && rule.type === RetentionRuleType.ZERO),
    [retentionPoliciesResponse],
  )

  const { data: documentsResponse, isFetching: documentsFetching } = useGetDocumentsQuery(
    accountHasZDRPolicy
      ? skipToken
      : {
          accountId,
          pageRequest: { page: page - 1, size: pageSize },
          ...(contactIds.length ? { contactIds } : {}),
          ...(documentTypeId ? { documentTypeId } : {}),
          ...(activeSearchTerm ? { searchTerm: activeSearchTerm } : {}),
          ...(effectiveCreatedByUserIds.length ? { createdByUserIds: effectiveCreatedByUserIds } : {}),
          ...(startDate && endDate ? { createdAfter: startDate.toISOString(), createdBefore: endDate.toISOString() } : {}),
        },
  )

  const documents = useMemo(() => documentsResponse?.data ?? [], [documentsResponse?.data])

  const groupedDocuments = useMemo(() => groupByBatch(documents), [documents])

  return {
    documentsResponse,
    documents,
    groupedDocuments,
    isLoading: documentsFetching || retentionPoliciesFetching,
    accountHasZDRPolicy,
  }
}

export interface UnprocessedEmailAttachmentItem {
  attachment: EmailAttachmentMetadata
}

const _UNPROCESSED_MAX_AGE_DAYS = 30

export const dedupeEmailAttachments = (attachments: EmailAttachmentMetadata[]): EmailAttachmentMetadata[] => {
  const seen = new Set<string>()
  const byId = attachments.filter((attachment) => {
    const key = emailAttachmentQueueKey(attachment.emailExternalId, attachment.externalId)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  // two unrelated files sharing same name/type/size should be exceedingly rare
  const latestByContent = new Map<string, EmailAttachmentMetadata>()
  for (const attachment of byId) {
    const contentKey = `${attachment.fileName}::${attachment.contentType}::${attachment.sizeBytes}`
    const latest = latestByContent.get(contentKey)
    if (!latest || new Date(attachment.createdAt) > new Date(latest.createdAt)) {
      latestByContent.set(contentKey, attachment)
    }
  }

  return byId.filter((attachment) => {
    const contentKey = `${attachment.fileName}::${attachment.contentType}::${attachment.sizeBytes}`
    return latestByContent.get(contentKey)?.externalId === attachment.externalId
  })
}

export const useUnprocessedEmailAttachments = (contactId: string | null, excludeKeys: ReadonlySet<string>) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: me, isFetching: meFetching } = useGetMeQuery({ accountId })

  const emailConnector = useMemo(() => me?.integrations?.find((integration) => integration.type === 'mail')?.serviceId ?? null, [me])

  const [createdAfter] = useState(() => new Date(Date.now() - _UNPROCESSED_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toISOString())

  const { data: attachmentsResponse, isFetching: attachmentsFetching } = useGetEmailAttachmentsQuery(
    emailConnector && contactId ? { accountId, connector: emailConnector, contactId, createdAfter } : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  const attachments = useMemo(() => attachmentsResponse?.data ?? [], [attachmentsResponse])

  const dedupedAttachments = useMemo(() => dedupeEmailAttachments(attachments), [attachments])

  const referenceKeys = useMemo(
    () => dedupedAttachments.map((attachment) => ({ emailId: attachment.emailExternalId, attachmentId: attachment.externalId })),
    [dedupedAttachments],
  )

  const { data: referencesResponse, isFetching: referencesFetching } = useGetEmailAttachmentReferencesQuery(
    emailConnector && referenceKeys.length ? { accountId, connector: emailConnector, emailAttachments: referenceKeys } : skipToken,
  )

  const referencedKeys = useMemo(() => {
    const set = new Set<string>()
    for (const reference of referencesResponse?.data ?? []) {
      if (reference.state !== DocumentState.READY) {
        continue
      }
      set.add(emailAttachmentQueueKey(reference.emailId, reference.attachmentId))
    }
    return set
  }, [referencesResponse])

  const items = useMemo<UnprocessedEmailAttachmentItem[]>(
    () =>
      dedupedAttachments
        .filter((attachment) => {
          const key = emailAttachmentQueueKey(attachment.emailExternalId, attachment.externalId)
          return !referencedKeys.has(key) && !excludeKeys.has(key)
        })
        .map((attachment) => ({ attachment })),
    [dedupedAttachments, referencedKeys, excludeKeys],
  )

  const hasData = attachmentsResponse !== undefined && (referenceKeys.length === 0 || referencesResponse !== undefined)

  return {
    items,
    connector: emailConnector,
    isNotConnected: !meFetching && !emailConnector,
    isLoading: meFetching || attachmentsFetching || referencesFetching,
    hasData,
    hasAnyUnprocessedAttachments: dedupedAttachments.length > 0,
  }
}

export const useDocumentProcessingStatus = (document: DocumentRecord) => {
  const accountId = useAppSelector(selectAccountId)

  const { id: documentId, summaryProcessing } = document

  const summaryProcessingFinished = !!summaryProcessing.finishedAt

  const { data: aiAgentResult, isFetching: aiAgentResultFetching } = useGetAiAgentResultQuery(
    summaryProcessingFinished ? { accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: documentId } : skipToken,
  )

  const { data: contactChangesResponse, isFetching: contactChangesFetching } = useGetAllContactChangesQuery(
    summaryProcessingFinished ? { accountId, filters: { type: 'document', typeKey: documentId } } : skipToken,
  )

  const isLoading = useMemo(() => aiAgentResultFetching || contactChangesFetching, [aiAgentResultFetching, contactChangesFetching])

  const contactChanges = useMemo(() => contactChangesResponse?.data ?? [], [contactChangesResponse])

  const processingStatusType = useMemo(() => {
    if (!summaryProcessingFinished) {
      return DocumentProcessingStatusType.PROCESSING
    }

    if (isLoading) {
      return DocumentProcessingStatusType.LOADING
    }

    const aiAgentResultHasSyncStatus = aiAgentResult?.syncStatus.length
    const contactChangeHasSyncStatus = contactChanges.some((contactChange) => contactChange.syncStatus?.length)

    if (aiAgentResultHasSyncStatus || contactChangeHasSyncStatus) {
      return DocumentProcessingStatusType.SAVED
    }

    return DocumentProcessingStatusType.READY
  }, [aiAgentResult, contactChanges, isLoading, summaryProcessingFinished])

  return { processingStatusType }
}

const _PAGE_PARAM_KEY = 'page'
const _PAGE_SIZE_PARAM_KEY = 'pageSize'
const _SEARCH_TERM_PARAM_KEY = 'term'

export const useDocumentSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = useMemo(() => parseInt(searchParams.get(_PAGE_PARAM_KEY) ?? '1'), [searchParams])

  const pageSize = useMemo(() => parseInt(searchParams.get(_PAGE_SIZE_PARAM_KEY) ?? '20'), [searchParams])

  const searchTerm = useMemo(() => searchParams.get(_SEARCH_TERM_PARAM_KEY) ?? '', [searchParams])

  const handlePageChange = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set(_PAGE_PARAM_KEY, String(page))

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set(_PAGE_SIZE_PARAM_KEY, String(pageSize))

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleSearchTermChange = useCallback(
    (searchTerm: string) => {
      const newSearchParams = new URLSearchParams(searchParams)
      if (!searchTerm) {
        newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
      } else {
        newSearchParams.set(_SEARCH_TERM_PARAM_KEY, searchTerm)
      }

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleClearSearchTerm = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
    setSearchParams(newSearchParams)
  }, [searchParams, setSearchParams])

  return { page, pageSize, searchTerm, handlePageChange, handlePageSizeChange, handleSearchTermChange, handleClearSearchTerm }
}

export type DocumentMatchedBy = 'both' | 'name' | 'hash'

interface DocumentMatch {
  document?: DocumentRecord
  matchedBy?: DocumentMatchedBy
}

export const useExistingDocument = (contactId: string, fileName: string, fileHash: string) => {
  const accountId = useAppSelector(selectAccountId)
  const accountHasZDRPolicy = useAccountHasDocumentZdrPolicy()

  const { data: byNameResponse, isFetching: byNameFetching } = useGetAllDocumentsQuery(
    accountHasZDRPolicy ? skipToken : { accountId, contactIds: [contactId], fileName },
  )

  const { data: byHashResponse, isFetching: byHashFetching } = useGetAllDocumentsQuery(
    accountHasZDRPolicy ? skipToken : { accountId, contactIds: [contactId], fileHash },
  )

  const documentsByName = useMemo(() => byNameResponse?.data ?? [], [byNameResponse])

  const documentsByHash = useMemo(() => byHashResponse?.data ?? [], [byHashResponse])

  const match = useMemo<DocumentMatch>(() => {
    const documentsByHashIds = new Set(documentsByHash.map((document) => document.id))

    const both = documentsByName.find((document) => documentsByHashIds.has(document.id))
    if (both) {
      return { document: both, matchedBy: 'both' }
    }

    if (documentsByName.length) {
      return { document: documentsByName[0], matchedBy: 'name' }
    }

    if (documentsByHash.length) {
      return { document: documentsByHash[0], matchedBy: 'hash' }
    }

    return {}
  }, [documentsByHash, documentsByName])

  return { document: match.document, matchedBy: match.matchedBy, isLoading: byNameFetching || byHashFetching }
}
